import { EventSourcePolyfill } from 'event-source-polyfill';
import { getAccessToken } from './accessToken';
import { ensureAuthSession } from './ensureAuthSession';
import { isCookieAuthMode } from './authMode';

/**
 * Centralized helper to open authenticated SSE connections using EventSourcePolyfill.
 * - Returns a synchronous EventSource-compatible wrapper
 * - Refreshes the auth session before opening the underlying stream
 * - Sends cookies with withCredentials: true for HttpOnly cookie auth mode
 * - Injects Authorization: Bearer <token> if available and not already set
 * - Preserves provided headers and options
 */
export function openAuthenticatedEventSource(url, options = {}) {
  let activeEventSource = null;
  let closed = false;
  const listeners = new Map();
  const fallbackToken = getAccessToken();

  const wrapper = {
    onmessage: null,
    onerror: null,
    onopen: null,
    close() {
      closed = true;
      if (activeEventSource?.close) {
        activeEventSource.close();
      }
      activeEventSource = null;
    },
    addEventListener(eventName, handler) {
      if (!listeners.has(eventName)) {
        listeners.set(eventName, new Set());
      }
      listeners.get(eventName).add(handler);
      if (activeEventSource?.addEventListener) {
        activeEventSource.addEventListener(eventName, handler);
      }
    },
    removeEventListener(eventName, handler) {
      listeners.get(eventName)?.delete(handler);
      if (activeEventSource?.removeEventListener) {
        activeEventSource.removeEventListener(eventName, handler);
      }
    },
  };

  const openStream = (token) => {
    if (closed) return;

    const headers = { ...(options.headers || {}) };
    if (token && !('Authorization' in headers)) {
      headers.Authorization = `Bearer ${token}`;
    }

    const eventSource = new EventSourcePolyfill(url, {
      ...options,
      withCredentials: true,
      headers,
    });

    activeEventSource = eventSource;
    eventSource.onmessage = (event) => {
      if (!closed) wrapper.onmessage?.(event);
    };
    eventSource.onerror = (event) => {
      if (!closed) wrapper.onerror?.(event);
    };
    eventSource.onopen = (event) => {
      if (!closed) wrapper.onopen?.(event);
    };
    listeners.forEach((handlers, eventName) => {
      handlers.forEach((handler) => {
        eventSource.addEventListener(eventName, handler);
      });
    });
  };

  const notifyOpenFailure = (error) => {
    if (!closed) {
      wrapper.onerror?.({
        type: 'error',
        error,
        reason: 'auth_session_refresh_failed',
        retryable: true,
      });
    }
  };

  ensureAuthSession({ forceRefresh: true })
    .catch((error) => {
      if (!isCookieAuthMode() && fallbackToken) {
        return fallbackToken;
      }
      throw error;
    })
    .then(openStream)
    .catch(notifyOpenFailure);

  return wrapper;
}

